import { FC, memo } from 'react';
import { useSelector } from 'react-redux';
import { gql } from '@apollo/client';

import { selectIsBrandAffinityFeatureFlagOn } from 'core/userContext';
import { useCampaignsDataQuery, useTemplatesPreviewQuery } from 'modules/ai/hooks/campaigns';

import { BrandAffinityXAIContext } from './BrandAffinityXAIContext';
import { useXAIUserProfileQuery } from '../hooks/useXAIUserProfileQuery';
import {
  getTemplateIds,
  defaultXAIData,
  getCampaignIds,
  createCombinedCampaignData,
} from '../utils/context';
import { CampaignData } from '../../types';

export const fetchCampaignsData = gql`
  query getCampaigns($ids: [Long!]) {
    campaigns(ids: $ids) {
      results {
        id
        name
        templateId
        __typename
      }
      __typename
    }
  }
`;

export interface BrandAffinityXAIContextProps {
  userUuidKey: string | undefined;
  userEmailKey: string | undefined;
  brandAffinityLabel?: string | undefined;
}

export const BrandAffinityXAIContextProvider: FC<
  React.PropsWithChildren<BrandAffinityXAIContextProps>
> = memo(({ userUuidKey, userEmailKey, brandAffinityLabel, children }) => {
  const isBrandAffinityEnabled = useSelector(selectIsBrandAffinityFeatureFlagOn);

  if (!isBrandAffinityEnabled) {
    return (
      <BrandAffinityXAIContext.Provider
        value={{
          isLoading: false,
          isError: false,
          affinityData: defaultXAIData,
          campaignsData: [],
          previewData: {},
        }}
      >
        {children}
      </BrandAffinityXAIContext.Provider>
    );
  }

  const {
    isLoading: isXAIRequestLoading,
    isError: isXAIRequestError,
    data: parsedAffinityData,
  } = useXAIUserProfileQuery(userUuidKey, userEmailKey, brandAffinityLabel);

  const parsedCampaignIds = getCampaignIds(parsedAffinityData);
  const {
    isLoading: isCampaignsLoading,
    isError: isCampaignsError,
    data: parsedCampaignData,
  } = useCampaignsDataQuery<CampaignData>(
    fetchCampaignsData,
    isXAIRequestLoading,
    isXAIRequestError,
    parsedCampaignIds,
  );
  const parsedTemplateIds = getTemplateIds(parsedCampaignData);
  const {
    isLoading: isTemplatesPreviewLoading,
    isError: isTemplatesPreviewError,
    data: previewData,
  } = useTemplatesPreviewQuery(
    parsedCampaignData !== undefined,
    parsedTemplateIds,
    'XAItemplatesPreview',
  );

  return (
    <BrandAffinityXAIContext.Provider
      value={{
        isLoading: isXAIRequestLoading || isCampaignsLoading || isTemplatesPreviewLoading,
        isError: isXAIRequestError || isCampaignsError || isTemplatesPreviewError,
        affinityData: parsedAffinityData,
        campaignsData: createCombinedCampaignData(parsedAffinityData, parsedCampaignData),
        previewData,
      }}
    >
      {children}
    </BrandAffinityXAIContext.Provider>
  );
});
